import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import { useFindAllQuestionsQuery } from "../../../../graphql/generated.tsx";
import ImportQuestionCard from "../../Components/ImportQuestionCard";
import { tagsData } from "../../../../Mockdata/tags.js";
import Pagination from "../../../../components/Pagination";

const difficultyLevels = ["EASY", "MEDIUM", "HARD"];

const ImportModuleQuestion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dayIndex = Number(searchParams.get("day")) || 0;
  const existingQuestionIds = useMemo(
    () => new Set(location.state?.existingQuestionIds || []),
    [location.state?.existingQuestionIds]
  );

  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data: questionsData, refetch, loading } = useFindAllQuestionsQuery({
    variables: {
      page,
      limit: pageSize,
      searchName: searchQuery,
      tags: selectedTags,
      difficulties: selectedDifficulty,
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    refetch();
  }, [page, searchQuery, selectedTags, selectedDifficulty, refetch]);

  const questions = useMemo(() => questionsData?.findAllQuestions || [], [questionsData]);
  const totalPages = useMemo(() => {
    if (!questionsData) return 1;
    return questions.length < pageSize ? page : page + 1;
  }, [questionsData, questions.length, page, pageSize]);

  const handleCheckboxChange = (questionId) => {
    if (existingQuestionIds.has(questionId)) return;
    setSelectedQuestions((prev) =>
      prev.includes(questionId) ? prev.filter((id) => id !== questionId) : [...prev, questionId]
    );
  };

  const handleImport = () => {
    if (selectedQuestions.length === 0) return;
    const payload = questions
      .filter((q) => selectedQuestions.includes(q.id))
      .map((q) => ({
        id: q.id,
        title: q.title,
        description: q.question_sub_text || "",
        difficulty: q.difficulty,
        tags: q.tags || [],
        type: q.category?.name || "",
        topic: q.company_tags?.[0] || "",
      }));

    sessionStorage.setItem(
      "moduleImportQuestions",
      JSON.stringify({ dayIndex, questions: payload })
    );
    navigate(-1);
  };

  const handleTagSelect = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
    setPage(1);
  };

  const handleDifficultySelect = (level) => {
    setSelectedDifficulty((prev) =>
      prev.includes(level) ? prev.filter((d) => d !== level) : [...prev, level]
    );
    setPage(1);
  };

  return (
    <div className="w-full min-h-screen bg-white">
      <NetworkIndicator />
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <span className="flex items-center gap-2 text-sm font-medium">Go Back</span>
          </button>
          <h2 className="text-2xl font-bold text-center">Import Questions</h2>
          <button
            onClick={handleImport}
            disabled={selectedQuestions.length === 0}
            className="px-4 py-2 rounded-md bg-[rgba(14,61,93,1)] text-white font-semibold disabled:opacity-50"
          >
            Import Selected
          </button>
        </div>

        <div className="flex items-center justify-center mb-4 gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search questions..."
            className="border border-gray-300 rounded-md px-3 py-2 w-[300px]"
          />
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-md bg-[rgba(14,61,93,1)] text-white font-semibold"
          >
            Search
          </button>
        </div>

        <div className="flex gap-6">
          <div className="flex-1">
            <div className="space-y-2">
              {loading ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div
                      key={`skeleton-${idx}`}
                      className="flex items-start gap-3 w-full px-4 py-3 bg-white rounded-md border border-gray-100 shadow-sm animate-pulse"
                    >
                      <div className="w-4 h-4 mt-2 rounded bg-gray-200" />
                      <div className="w-full">
                        <div className="h-4 w-2/3 bg-gray-200 rounded mb-2" />
                        <div className="h-3 w-1/2 bg-gray-200 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : questions.length === 0 ? (
                <div className="text-center text-gray-500 py-10">No questions found.</div>
              ) : (
                questions.map((question) => (
                  <div
                    key={question.id}
                    className={`flex items-start gap-3 w-full px-4 py-3 bg-white rounded-md border border-gray-100 shadow-sm ${existingQuestionIds.has(question.id) ? "opacity-60" : ""}`}
                  >
                    <input
                      type="checkbox"
                      onChange={() => handleCheckboxChange(question.id)}
                      checked={selectedQuestions.includes(question.id)}
                      disabled={existingQuestionIds.has(question.id)}
                      className="mt-2 accent-blue-600"
                    />
                    <div className="w-full">
                      <ImportQuestionCard data={question} />
                    </div>
                  </div>
                ))
              )}
            </div>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(nextPage) => setPage(nextPage)}
            />
          </div>

          <div className="w-64 p-6 bg-white shadow-lg rounded-lg h-fit">
            <div className="mb-4">
              <h3 className="mb-2 font-semibold">Filter by Tags</h3>
              {tagsData.map((tag) => (
                <label key={tag} className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag)}
                    onChange={() => handleTagSelect(tag)}
                    className="accent-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">{tag}</span>
                </label>
              ))}
            </div>
            <div>
              <h3 className="mb-2 font-semibold">Filter by Difficulty</h3>
              {difficultyLevels.map((level) => (
                <label key={level} className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={selectedDifficulty.includes(level)}
                    onChange={() => handleDifficultySelect(level)}
                    className="accent-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">{level}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportModuleQuestion;
